import { css, html, LitElement, nothing } from "lit";

import { formatDate, formatSize } from "../lib/format";
import {
  artifactActionIntent,
  type ArtifactActionDescriptor,
} from "../lib/artifact-actions";
import { controlStyles } from "../styles/tokens";
import type {
  ArtifactActionId,
  ArtifactActionIntent,
  ArtifactDetail,
  VersionRow,
} from "../transport/types";

/**
 * <modulina-versions-panel> — one artifact's version chain (spec-web-ui §2:
 * v-number/author/message/size/date, select a version, act on it).
 * Newest first for reading (the wire delivers versionNumber ASC —
 * presentation reverses, data untouched). Escape closes (spec keyboard
 * bar); the close event also drives the shell's history (deep-link URL
 * drops back to "/").
 *
 * Props in: artifact detail (+ loading flag) + the `actions` descriptors
 * (capability data — the footer renders one button per action). Events
 * out: `close` and `artifact-action` (detail: {@link ArtifactActionIntent},
 * targeting the SELECTED version). Zero transport knowledge.
 */
export class ModulinaVersionsPanel extends LitElement {
  static override properties = {
    artifact: { attribute: false },
    loading: { type: Boolean, attribute: false },
    actions: { attribute: false },
    selectedVersion: { state: true },
  };

  declare artifact: ArtifactDetail | null;
  declare loading: boolean;
  /** Per-artifact actions to render as footer buttons (capability data). */
  declare actions: ArtifactActionDescriptor[];
  /** null = the current (newest) version. */
  declare selectedVersion: number | null;

  constructor() {
    super();
    this.artifact = null;
    this.loading = false;
    this.actions = [];
    this.selectedVersion = null;
    // Focusable host: opening the panel moves focus here so Escape closes
    // it immediately (spec keyboard bar) without an extra Tab stop.
    this.tabIndex = -1;
  }

  protected override updated(changed: Map<string, unknown>): void {
    if (changed.has("artifact") && this.artifact) {
      this.selectedVersion = null; // a new artifact resets the pick
      this.focus();
    }
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        background: var(--m-color-surface);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-3) var(--m-space-4);
      }
      header {
        display: flex;
        align-items: baseline;
        gap: var(--m-space-2);
        margin-bottom: var(--m-space-2);
      }
      h2 {
        font-size: var(--m-fs-base);
        margin: 0;
        word-break: break-word;
      }
      .meta {
        color: var(--m-color-text-muted);
        font-size: var(--m-fs-small);
      }
      .spacer {
        flex: 1;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: 30vh;
        overflow-y: auto;
      }
      li {
        display: flex;
        align-items: center;
        gap: var(--m-space-2);
        padding: var(--m-space-1) var(--m-space-2);
        border-radius: var(--m-radius-small);
        cursor: pointer;
        font-size: var(--m-fs-small);
      }
      li:hover {
        background: var(--m-color-accent-soft);
      }
      li.selected {
        background: var(--m-color-selected);
      }
      li .vmeta {
        color: var(--m-color-text-muted);
        word-break: break-word;
      }
      .empty {
        color: var(--m-color-text-muted);
        font-size: var(--m-fs-small);
        padding: var(--m-space-2);
      }
      footer {
        display: flex;
        gap: var(--m-space-2);
        margin-top: var(--m-space-2);
      }
    `,
  ];

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.onKeydown);
  }

  override disconnectedCallback() {
    this.removeEventListener("keydown", this.onKeydown);
    super.disconnectedCallback();
  }

  override render() {
    if (this.loading) {
      return html`<div class="empty" aria-live="polite">Kraunamos versijos…</div>`;
    }
    const artifact = this.artifact;
    if (!artifact) return nothing;
    const newestFirst = [...artifact.versions].reverse();
    return html`
      <header>
        <h2>${artifact.name}</h2>
        <span class="meta">${artifact.type} · ${newestFirst.length} versij${lithuanianPluralSuffix(newestFirst.length)}</span>
        <span class="spacer"></span>
        <button @click=${this.emitClose} title="Uždaryti (Esc)">Uždaryti</button>
      </header>
      ${newestFirst.length === 0
        ? html`<div class="empty">Versijų nėra.</div>`
        : html`<ul role="listbox" aria-label="Versijos">
            ${newestFirst.map((v) => this.renderVersion(v, artifact))}
          </ul>`}
      <footer>
        ${this.actions.map(
          (action, i) => html`<button
            class=${i === 0 ? "primary" : ""}
            ?disabled=${newestFirst.length === 0}
            @click=${() => this.emitAction(action.id)}
          >
            ${action.label} ${this.selectedLabel(artifact)}
          </button>`,
        )}
      </footer>
    `;
  }

  private renderVersion(v: VersionRow, artifact: ArtifactDetail) {
    const isCurrent = v.id === artifact.currentVersionId;
    const isSelected =
      this.selectedVersion === null ? isCurrent : v.versionNumber === this.selectedVersion;
    const meta = [v.author, v.message ?? "", formatSize(v.sizeBytes), formatDate(v.createdAt)]
      .filter((x) => x !== "")
      .join(" · ");
    return html`<li
      class=${isSelected ? "selected" : ""}
      role="option"
      aria-selected=${isSelected}
      tabindex="0"
      @click=${() => this.selectVersion(v, isCurrent)}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.selectVersion(v, isCurrent);
        }
      }}
    >
      <strong>v${v.versionNumber}${isCurrent ? " (dabartinė)" : ""}</strong>
      <span class="vmeta">${meta}</span>
    </li>`;
  }

  private selectedLabel(artifact: ArtifactDetail): string {
    if (this.selectedVersion === null) return "naujausią";
    const current = artifact.versions.find((v) => v.id === artifact.currentVersionId);
    return this.selectedVersion === current?.versionNumber
      ? "naujausią"
      : `v${this.selectedVersion}`;
  }

  private selectVersion(v: VersionRow, isCurrent: boolean) {
    this.selectedVersion = isCurrent ? null : v.versionNumber;
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.emitClose();
  };

  private emitClose = () => {
    this.selectedVersion = null;
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  };

  /** Emit the typed intent for `action` on the SELECTED version (null
   * selection = the current version → omitted versionNumber). */
  private emitAction(action: ArtifactActionId): void {
    const artifact = this.artifact;
    if (!artifact) return;
    this.dispatchEvent(
      new CustomEvent<ArtifactActionIntent>("artifact-action", {
        detail: artifactActionIntent(action, artifact.id, this.selectedVersion ?? undefined),
        bubbles: true,
        composed: true,
      }),
    );
  }
}

/** "1 versija / 2-9 versijos / 10-20 versijų" — the LT plural rule for
 * the header count (good-enough form for 0..n; exact grammar tested by eye). */
function lithuanianPluralSuffix(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "a";
  if (mod10 >= 2 && mod10 <= 9 && (mod100 < 11 || mod100 > 19)) return "os";
  return "ų";
}

customElements.define("modulina-versions-panel", ModulinaVersionsPanel);
